import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { getCurrencySymbol } from '../lib/currency';

import { toast } from 'sonner';
import {
  Trophy, Check, X, Loader2, Clock, DollarSign, AlertCircle, Archive,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtHours = (v) => `${Math.round((v || 0) * 10) / 10} hrs`;
const fmtCurrency = (v, sym = '$') => `${sym}${Math.round(v || 0).toLocaleString()}`;

function DeltaChip({ value, format = 'number', invert = false, sym = '$' }) {
  if (!value || value === 0) {
    return <span className="text-[11px] text-slate-400">No change</span>;
  }
  const positive = invert ? value < 0 : value > 0;
  const color = positive ? 'text-red-600 bg-red-50' : 'text-emerald-700 bg-emerald-50';
  const formatted = format === 'currency' ? `${sym}${Math.round(Math.abs(value)).toLocaleString()}`
    : format === 'hours' ? `${Math.round(Math.abs(value) * 10) / 10} hrs`
    : format === 'percent' ? `${Math.abs(value).toFixed(1)}%`
    : String(Math.abs(value));
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${color}`}>
      {value > 0 ? '+' : '-'}{formatted}
    </span>
  );
}

export default function WinnerApprovalPage() {
  const { token } = useParams();
  const [nomination, setNomination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [decisionNote, setDecisionNote] = useState('');
  const [decidedByName, setDecidedByName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/winner-approval/${token}`)
      .then((res) => {
        setNomination(res.data);
        setDecidedByName(res.data?.stakeholder_name || '');
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'Link not valid');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const submitDecision = async (action) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/winner-approval/${token}/decide`, {
        action,
        decision_note: decisionNote,
        decided_by_name: decidedByName,
      });
      toast.success(res.data?.message || `Nomination ${action}d`);
      // Refresh the nomination to reflect status.
      const refreshed = await axios.get(`${API_URL}/api/winner-approval/${token}`);
      setNomination(refreshed.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not ${action}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="winner-approval-error">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-slate-900 mb-1">Link not available</h1>
            <p className="text-sm text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const winnerId = nomination.winner_id;
  const summary = nomination.estimates_summary || [];
  const _wCodes = new Set(summary.map(e => (e.currency_code || 'USD').toUpperCase()));
  const pageSym = _wCodes.size === 1 ? getCurrencySymbol([..._wCodes][0]) : '$';
  const winner = summary.find((e) => e.id === winnerId) || summary[0] || {};
  const losers = summary.filter((e) => e.id !== winnerId);
  const diffs = (nomination.diff_summary || {}).diffs || [];
  const status = nomination.status || 'pending';
  const decided = status !== 'pending';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-10 px-4" data-testid="winner-approval-page">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0F2040] flex items-center justify-center">
            <Trophy className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Seertech Blueprint Studio</div>
            <h1 className="text-xl font-semibold text-slate-900">Winning-estimate approval</h1>
          </div>
        </div>

        {decided ? (
          <Card
            className={status === 'approved' ? 'border-emerald-300 bg-emerald-50/40' : 'border-red-300 bg-red-50/40'}
            data-testid="winner-approval-decided"
          >
            <CardContent className="p-5 flex items-start gap-3">
              {status === 'approved' ? (
                <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
              ) : (
                <X className="w-5 h-5 text-red-600 mt-0.5" />
              )}
              <div>
                <div className="font-semibold text-slate-900 capitalize">{status}</div>
                <div className="text-xs text-slate-600 mt-0.5">
                  {nomination.decided_by_name || nomination.stakeholder_name || 'Stakeholder'}
                  {nomination.decided_at ? ` · ${new Date(nomination.decided_at).toLocaleString()}` : ''}
                </div>
                {nomination.decision_note && (
                  <div className="text-sm text-slate-700 mt-2 italic">"{nomination.decision_note}"</div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="p-5 text-sm text-slate-700">
              <strong className="text-slate-900">{nomination.nominated_by_name || 'A project manager'}</strong> is
              asking you to sign off on the winning estimate.
              {nomination.note && (
                <blockquote className="border-l-2 border-amber-400 pl-3 mt-3 text-slate-600 italic">
                  {nomination.note}
                </blockquote>
              )}
            </CardContent>
          </Card>
        )}

        {/* Winner card */}
        <Card data-testid="winner-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" /> Nominated winner
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5 pt-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-lg font-semibold text-slate-900" data-testid="winner-name">{winner.estimate_name}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {winner.implementation_type} · {winner.complexity}
                </div>
              </div>
              <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200">Winner</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="border border-slate-200 rounded p-3">
                <div className="flex items-center gap-1 text-[11px] text-slate-500 uppercase tracking-wide mb-1"><Clock className="w-3 h-3" /> Effort</div>
                <div className="text-base font-semibold text-slate-900">{fmtHours(winner.total_effort)}</div>
              </div>
              <div className="border border-slate-200 rounded p-3">
                <div className="flex items-center gap-1 text-[11px] text-slate-500 uppercase tracking-wide mb-1"><DollarSign className="w-3 h-3" /> Cost</div>
                <div className="text-base font-semibold text-slate-900">{fmtCurrency(winner.total_cost, pageSym)}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Losers */}
        {losers.length > 0 && (
          <Card data-testid="losers-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Archive className="w-4 h-4 text-slate-500" /> Will be archived on approval
                <Badge variant="secondary" className="text-[10px]">{losers.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {losers.map((l) => {
                  const d = diffs.find((x) => x.loser_id === l.id);
                  const s = d?.diff?.summary || {};
                  return (
                    <div key={l.id} className="p-4 flex items-center justify-between gap-3" data-testid={`loser-row-${l.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{l.estimate_name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {fmtHours(l.total_effort)} · {fmtCurrency(l.total_cost, pageSym)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-[11px] text-slate-500 text-right">vs winner</div>
                        <ArrowRight className="w-3 h-3 text-slate-400" />
                        <DeltaChip value={s.effort_delta} format="hours" />
                        <DeltaChip value={s.cost_delta} format="currency" sym={pageSym} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Decision form */}
        {!decided && (
          <Card data-testid="decision-form">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Your decision</CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-0 space-y-4">
              <div>
                <Label htmlFor="decided-name" className="text-xs font-medium text-slate-700">Your name</Label>
                <Input
                  id="decided-name"
                  value={decidedByName}
                  onChange={(e) => setDecidedByName(e.target.value)}
                  placeholder="Jane Sponsor"
                  className="mt-1"
                  data-testid="decision-name-input"
                />
              </div>
              <div>
                <Label htmlFor="decision-note" className="text-xs font-medium text-slate-700">Note (optional)</Label>
                <Textarea
                  id="decision-note"
                  value={decisionNote}
                  onChange={(e) => setDecisionNote(e.target.value)}
                  placeholder="Any context to leave with this decision…"
                  rows={3}
                  className="mt-1"
                  data-testid="decision-note-input"
                />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => submitDecision('reject')}
                  disabled={submitting}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="decision-reject-btn"
                >
                  <X className="w-4 h-4 mr-1" /> Reject
                </Button>
                <Button
                  onClick={() => submitDecision('approve')}
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="decision-approve-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  Approve winner
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-center text-[11px] text-slate-400 pt-4">
          Powered by Seertech Blueprint Studio
        </div>
      </div>
    </div>
  );
}
